// Serviço de API para OAuth e Integrações com Marketplaces
//
// CORRIGIDO (achado da varredura - mesmo padrão do Painel do Robô):
// antes usava fetch() puro, que em tese já recebia o token automaticamente
// pelo interceptador global (httpAuthConfig.js), mas na prática a tela de
// Integrações do afiliado ficava sempre vazia (0 marketplaces, 0 conexões).
// Convertido para usar authService.fetchAuth explicitamente, o mesmo
// método já comprovado funcionando no Painel do Robô.
import authService from "./authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const oauthService = {
  // ======================
  // MARKETPLACES
  // ======================

  // Listar todos os marketplaces disponíveis
  async getMarketplaces(includeComingSoon = false) {
    try {
      const response = await authService.fetchAuth(
        `${BACKEND_URL}/api/oauth/marketplaces?include_coming_soon=${includeComingSoon}`
      );
      if (!response.ok) throw new Error(`Erro ao buscar marketplaces (${response.status})`);
      return await response.json();
    } catch (error) {
      console.error('Erro ao buscar marketplaces:', error);
      throw error;
    }
  },

  // Obter detalhes de um marketplace
  async getMarketplaceDetails(marketplaceId) {
    try {
      const response = await authService.fetchAuth(`${BACKEND_URL}/api/oauth/marketplaces/${marketplaceId}`);
      if (!response.ok) throw new Error('Marketplace não encontrado');
      return await response.json();
    } catch (error) {
      console.error('Erro ao buscar marketplace:', error);
      throw error;
    }
  },

  // ======================
  // AUTHORIZATION FLOW
  // ======================

  // Iniciar autorização OAuth
  async startAuthorization(userId, userType, marketplaceId, redirectAfter = '/dashboard') {
    try {
      const response = await authService.fetchAuth(`${BACKEND_URL}/api/oauth/authorize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          user_type: userType,
          marketplace_id: marketplaceId,
          redirect_after: redirectAfter
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Erro ao iniciar autorização');
      }

      return await response.json();
    } catch (error) {
      console.error('Erro ao iniciar OAuth:', error);
      throw error;
    }
  },

  // Conectar com API Key (Shein, Havan, etc.)
  async connectWithApiKey(userId, userType, marketplaceId, credentials) {
    try {
      const response = await authService.fetchAuth(`${BACKEND_URL}/api/oauth/connect-api-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          user_type: userType,
          marketplace_id: marketplaceId,
          credentials: credentials
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Erro ao conectar');
      }

      return await response.json();
    } catch (error) {
      console.error('Erro ao conectar com API Key:', error);
      throw error;
    }
  },

  // ======================
  // USER CONNECTIONS
  // ======================

  // Listar conexões do usuário
  async getUserConnections(userId) {
    try {
      const response = await authService.fetchAuth(`${BACKEND_URL}/api/oauth/connections/${userId}`);
      if (!response.ok) throw new Error(`Erro ao buscar conexões (${response.status})`);
      return await response.json();
    } catch (error) {
      console.error('Erro ao buscar conexões:', error);
      throw error;
    }
  },

  // Verificar status de uma conexão
  async getConnectionStatus(userId, marketplaceId) {
    try {
      const response = await authService.fetchAuth(
        `${BACKEND_URL}/api/oauth/connection/${userId}/${marketplaceId}`
      );
      if (!response.ok) throw new Error('Erro ao verificar conexão');
      return await response.json();
    } catch (error) {
      console.error('Erro ao verificar conexão:', error);
      throw error;
    }
  },

  // Desconectar marketplace
  async disconnect(userId, marketplaceId) {
    try {
      const response = await authService.fetchAuth(`${BACKEND_URL}/api/oauth/disconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          marketplace_id: marketplaceId
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Erro ao desconectar');
      }

      return await response.json();
    } catch (error) {
      console.error('Erro ao desconectar:', error);
      throw error;
    }
  },

  // Remover conexão completamente
  async deleteConnection(userId, marketplaceId) {
    try {
      const response = await authService.fetchAuth(
        `${BACKEND_URL}/api/oauth/connection/${userId}/${marketplaceId}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Erro ao remover conexão');
      }

      return await response.json();
    } catch (error) {
      console.error('Erro ao remover conexão:', error);
      throw error;
    }
  },

  // Forçar refresh de token
  async refreshToken(userId, marketplaceId) {
    try {
      const response = await authService.fetchAuth(
        `${BACKEND_URL}/api/oauth/refresh/${userId}/${marketplaceId}`,
        { method: 'POST' }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Erro ao atualizar token');
      }

      return await response.json();
    } catch (error) {
      console.error('Erro ao refresh token:', error);
      throw error;
    }
  },

  // ======================
  // ADMIN: CREDENTIALS
  // ======================

  // [ADMIN] Salvar credenciais do app
  async saveAppCredentials(marketplaceId, credentials, updatedBy) {
    try {
      const response = await authService.fetchAuth(`${BACKEND_URL}/api/oauth/admin/credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          marketplace_id: marketplaceId,
          credentials: credentials,
          updated_by: updatedBy
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Erro ao salvar credenciais');
      }

      return await response.json();
    } catch (error) {
      console.error('Erro ao salvar credenciais:', error);
      throw error;
    }
  },

  // [ADMIN] Verificar status de credenciais
  async getAppCredentialsStatus(marketplaceId) {
    try {
      const response = await authService.fetchAuth(
        `${BACKEND_URL}/api/oauth/admin/credentials/${marketplaceId}`
      );
      if (!response.ok) throw new Error('Erro ao buscar status');
      return await response.json();
    } catch (error) {
      console.error('Erro ao buscar status credenciais:', error);
      throw error;
    }
  },

  // [ADMIN] Listar status de todas as credenciais
  async getAllCredentialsStatus() {
    try {
      const response = await authService.fetchAuth(`${BACKEND_URL}/api/oauth/admin/credentials`);
      if (!response.ok) throw new Error('Erro ao buscar credenciais');
      return await response.json();
    } catch (error) {
      console.error('Erro ao buscar credenciais:', error);
      throw error;
    }
  },

  // ======================
  // PLATFORM CONNECTIONS (PRE-CONFIGURED FOR AFFILIATES)
  // ======================

  // Get platform-level connections (admin-managed, shared with all affiliates)
  async getPlatformConnections() {
    try {
      const response = await authService.fetchAuth(`${BACKEND_URL}/api/oauth/platform/connections`);
      if (!response.ok) throw new Error('Erro ao buscar conexões da plataforma');
      return await response.json();
    } catch (error) {
      console.error('Erro ao buscar conexões da plataforma:', error);
      throw error;
    }
  },

  // ======================
  // STATS & LOGS
  // ======================

  // Estatísticas de OAuth
  async getStats() {
    try {
      const response = await authService.fetchAuth(`${BACKEND_URL}/api/oauth/stats`);
      if (!response.ok) throw new Error('Erro ao buscar stats');
      return await response.json();
    } catch (error) {
      console.error('Erro ao buscar stats:', error);
      throw error;
    }
  },

  // Logs de OAuth
  async getLogs(userId = null, marketplaceId = null, limit = 100) {
    try {
      let url = `${BACKEND_URL}/api/oauth/logs?limit=${limit}`;
      if (userId) url += `&user_id=${userId}`;
      if (marketplaceId) url += `&marketplace_id=${marketplaceId}`;

      const response = await authService.fetchAuth(url);
      if (!response.ok) throw new Error('Erro ao buscar logs');
      return await response.json();
    } catch (error) {
      console.error('Erro ao buscar logs:', error);
      throw error;
    }
  }
};

export default oauthService;
